import type { KeyboardEvent, ReactNode } from 'react';
import { FAILURE_CAUSE_INFO, MESSAGE_DELIVERY_STATE_LABELS, type MessageDeliveryRow } from '@nest-recruit/shared';
import { ToneChip } from '../../../../components/Tone';
import { formatDateTime } from '../../../../lib/format';
import { STATE_TONES, plural } from './email-delivery-address';
import styles from './MessageList.module.css';

/**
 * The emails, failures needing attention pinned above everything else (Option A: "never silent"). Each row says
 * who it went to and, for a failure, why in a few words. Arrow keys walk the list and open each email (06-qol §2).
 */
export function MessageList({
  rows,
  isPinned,
  openId,
  onOpen,
}: {
  readonly rows: readonly MessageDeliveryRow[];
  /** No pill chosen: failures needing attention head their own section. */
  readonly isPinned: boolean;
  readonly openId: string | undefined;
  readonly onOpen: (id: string) => void;
}): ReactNode {
  const pinned = isPinned ? rows.filter((row) => row.state === 'attention') : [];
  const rest = isPinned ? rows.filter((row) => row.state !== 'attention') : rows;
  const order = [...pinned, ...rest];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const at = order.findIndex((row) => row.id === openId);
    const next = order[at + (event.key === 'ArrowDown' ? 1 : -1)] ?? (at < 0 ? order[0] : undefined);
    if (!next) return;
    event.preventDefault();
    onOpen(next.id);
    document.getElementById(rowDomId(next.id))?.focus();
  };

  return (
    <div className={styles.list} onKeyDown={onKeyDown}>
      {pinned.length > 0 && (
        <>
          <h3 className={styles.section}>Needs attention · {pinned.length.toLocaleString()}</h3>
          <ul className={styles.rows} aria-label="Needs attention">
            {pinned.map((row) => (
              <Row key={row.id} row={row} isOpen={row.id === openId} onOpen={onOpen} />
            ))}
          </ul>
        </>
      )}
      {rest.length > 0 && (
        <>
          {pinned.length > 0 && <h3 className={styles.section}>Everything else · newest first</h3>}
          <ul className={styles.rows} aria-label="Emails">
            {rest.map((row) => (
              <Row key={row.id} row={row} isOpen={row.id === openId} onOpen={onOpen} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function rowDomId(id: string): string {
  return `email-${id}`;
}

function Row({ row, isOpen, onOpen }: { readonly row: MessageDeliveryRow; readonly isOpen: boolean; readonly onOpen: (id: string) => void }): ReactNode {
  const hasFailed = row.state === 'attention' || row.state === 'retrying';
  const why = row.cause ? `${row.smtpCode ? `${row.smtpCode} ` : ''}${FAILURE_CAUSE_INFO[row.cause].label}` : null;
  return (
    <li>
      <button
        type="button"
        id={rowDomId(row.id)}
        className={styles.row}
        data-state={row.state}
        aria-current={isOpen || undefined}
        onClick={() => onOpen(row.id)}
      >
        <span className={styles.subject}>{row.subject}</span>
        <span className={styles.when}>{formatDateTime(row.failedAt ?? row.sentAt ?? row.createdAt)}</span>
        <span className={styles.to}>
          {row.recipientName ? `${row.recipientName} — ` : ''}
          {row.toAddress}
          {hasFailed && why && (
            <span className={styles.why}>
              <span className={styles.sep}>{' · '}</span>
              {why}
              {row.attemptCount > 0 ? ` · ${plural(row.attemptCount, 'try', 'tries')}` : ''}
            </span>
          )}
        </span>
        <span className={styles.chip}>
          <ToneChip tone={STATE_TONES[row.state]}>{MESSAGE_DELIVERY_STATE_LABELS[row.state]}</ToneChip>
        </span>
      </button>
    </li>
  );
}
